import { createSlice } from '@reduxjs/toolkit'
import axios from '../../../lib/httpClient'
import { gaAddToCart, gaRemoveFromCart } from '../../shared/helpers/googleAnalytics'

const initialState = {
  debug: false,
  status: 'fresh',
  loaded: false,
  loading: false,
  error: null,
  open: false,
  user_id: null,
  customer: null,
  order: null,
  stripeCustomer: null,
  payment_success: null,
  order_confirmation_id: null,
  shipping_costs: null,
  paymentRequestOpen: false,
  // True while the customer needs telling that a cart change rebuilt the
  // checkout form under them (SS-2918). It lives in redux rather than in
  // component state on purpose: the rebuild UNMOUNTS the form, so anything
  // held below <Elements> dies with the very event it is meant to report.
  checkoutFormReset: false,
}

// Hiding the page's scrollbar widens the viewport, so on platforms with a
// classic scrollbar the layout would jump right while the drawer is open. Pad
// the body by the scrollbar's width for as long as scrolling is locked.
const lockPageScroll = (locked) => {
  const { body, documentElement } = document

  if (locked && !body.classList.contains('noscroll')) {
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth
    body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : ''
  } else if (!locked) {
    body.style.paddingRight = ''
  }

  body.classList.toggle('noscroll', locked)
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },

    setError: (state, action) => {
      state.error = action.payload
    },

    setOpen: (state, action) => {
      state.open = Boolean(action.payload)
      lockPageScroll(state.open)
    },

    setUserId: (state, action) => {
      state.user_id = action.payload
    },

    setCheckoutFormReset: (state, action) => {
      state.checkoutFormReset = Boolean(action.payload)
    },

    setPaymentSuccess: (state, action) => {
      state.payment_success = action.payload
    },

    setOrderConfirmationId: (state, action) => {
      state.order_confirmation_id = action.payload
    },

    setCustomer: (state, action) => {
      state.customer = action.payload
    },

    setOrder: (state, action) => {
      state.order = action.payload
    },

    setStripeCustomer: (state, action) => {
      state.stripeCustomer = action.payload
    },

    setShippingCosts: (state, action) => {
      state.shipping_costs = action.payload
    },

    setPaymentRequestOpen: (state, action) => {
      state.paymentRequestOpen = action.payload
    },
  },
})

export const {
  setCustomer,
  setOrder,
  setStripeCustomer,
  setCheckoutUrl,
  setOrderConfirmationId,
  setLoading,
  setError,
  setOpen,
  setCheckoutFormReset,
  setShippingCosts,
  setPaymentRequestOpen,
  setPaymentSuccess,
  setUserId,
} = cartSlice.actions

export const selectLoaded = (state) => state.cart.loaded
export const selectLoading = (state) => state.cart.loading
export const selectError = (state) => state.cart.error
export const selectStatus = (state) => state.cart.status
export const selectOpen = (state) => state.cart.open
export const selectUserId = (state) => state.cart.user_id
export const selectContents = (state) => state.cart.contents
export const selectCustomer = (state) => state.cart.customer
export const selectOrder = (state) => state.cart.order
export const selectShippingCosts = (state) => state.cart.shipping_costs
export const selectStripeCustomer = (state) => state.cart.stripeCustomer
export const selectPaymentSuccess = (state) => state.cart.payment_success
export const selectOrderConfirmationId = (state) => state.cart.order_confirmation_id
export const selectContainsAMealPlan = (state) => state.cart.contains_a_meal_plan
export const selectPaymentRequestOpen = (state) => state.cart.paymentRequestOpen
export const selectCheckoutFormReset = (state) => state.cart.checkoutFormReset

export const fetchCustomer = (userId, accessToken) => async (dispatch) => {
  axios({
    method: 'get',
    url: `/api/users/${userId}`,
    headers: { 'X-CSRF-Token': accessToken },
  })
    .then(({ data: customer }) => {
      dispatch(setCustomer(customer))
    })
}

export const createCart = async (
  accessToken,
  product,
  quantity,
  subscription,
  mutate,
  dispatch,
  membershipOption,
  membershipSku = '',
) => axios({
  method: 'post',
  url: '/api/cart',
  headers: { 'X-CSRF-Token': accessToken },
  data: {
    attributes: { user_id: null },
    cart: {
      contents: [
        {
          data: product,
          quantity,
          subscription,
        },
      ],
    },
    membership_option: membershipOption,
    membership_sku: membershipSku,
  },
})
  .then(({ data }) => {
    window.localStorage.setItem('cart', data.uuid)
    mutate()
  })
  .then(() => gaAddToCart(product, quantity))
  .then(() => dispatch(setLoading(false)))
  .catch((error) => {
    const data = error?.response?.data
    dispatch(setError({ message: data?.message || 'Unable to create cart', link: data?.link, link_text: data?.link_text }))
    dispatch(setLoading(false))
    throw error
  })

export const addItemToCart = async (
  accessToken,
  cart,
  product,
  quantity,
  subscription,
  mutate,
  dispatch,
  membershipOption,
  membershipSku = '',
) => axios({
  method: 'post',
  url: `/api/cart/${cart.uuid}/cart_items`,
  headers: { 'X-CSRF-Token': accessToken },
  data: {
    sku: product.sku,
    quantity,
    subscription,
    membership_option: membershipOption,
    membership_sku: membershipSku,
  },
})
  .then(() => mutate())
  .then(() => gaAddToCart(product, quantity))
  .then(() => {
    dispatch(setError(null))
    dispatch(setLoading(false))
  })
  .catch((error) => {
    const data = error?.response?.data
    if (data?.switch_lab_company) {
      dispatch(setError({ message: data.message, switch_lab_company: data.switch_lab_company, product }))
    } else if (data?.suggestions?.items_to_remove?.length > 0) {
      dispatch(setError({ message: data.message, suggestions: data.suggestions, product }))
    } else {
      dispatch(setError({ message: data?.message || 'Unable to add item to cart', link: data?.link, link_text: data?.link_text }))
    }
    dispatch(setLoading(false))
    throw error
  })

export const incrementCart = async (
  accessToken,
  cart,
  product,
  existingItem,
  mutate,
  dispatch,
  membershipOption = '',
  membershipSku = '',
) => {
  const quantity = existingItem.quantity + 1

  axios({
    method: 'patch',
    url: `/api/cart/${cart.uuid}/cart_items/${product.id}`,
    headers: { 'X-CSRF-Token': accessToken },
    data: { quantity, membership_option: membershipOption, membership_sku: membershipSku },
  })
    .then(() => mutate())
    .then(() => gaAddToCart(product, 1))
    .then(() => dispatch(setLoading(false)))
}

export const handleRemove = async (accessToken, cart, product, mutate, dispatch, quantity) => {
  axios({
    method: 'delete',
    url: `/api/cart/${cart.uuid}/cart_items/${product.id}`,
    headers: { 'X-CSRF-Token': accessToken },
  })
    .then((response) => {
      if (!response.data) {
        window.localStorage.removeItem('cart')
        dispatch(setOpen(false))
      }
      mutate()
    })
    .then(() => gaRemoveFromCart(product, quantity))
    .then(() => {
      dispatch(setError(null))
      dispatch(setLoading(false))
    })
}

export const applySuggestions = async (accessToken, cart, suggestions, product, mutate, dispatch) => {
  const { items_to_remove: itemsToRemove, incoming_product_sku: sku } = suggestions

  try {
    // Remove each overlapping item sequentially, tracking if cart is destroyed
    let cartDestroyed = false
    for (const item of itemsToRemove) {
      // eslint-disable-next-line no-await-in-loop
      const response = await axios({
        method: 'delete',
        url: `/api/cart/${cart.uuid}/cart_items/${item.product_id}`,
        headers: { 'X-CSRF-Token': accessToken },
      })
      if (!response.data) {
        cartDestroyed = true
        break
      }
    }

    if (cartDestroyed) {
      // Cart was destroyed when its last item was removed. Create a new cart with just the
      // incoming product — no overlap validation needed since it's the only item.
      window.localStorage.removeItem('cart')
      await createCart(accessToken, product, 1, false, mutate, dispatch)
    } else {
      // Cart still exists — add the incoming product to it
      await axios({
        method: 'post',
        url: `/api/cart/${cart.uuid}/cart_items`,
        headers: { 'X-CSRF-Token': accessToken },
        data: { sku, quantity: 1 },
      })
      mutate()
    }

    dispatch(setError(null))
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error applying cart suggestions:', error)
    dispatch(setError(error?.response?.data?.message || 'Something went wrong while applying suggestions. Please try again.'))
  } finally {
    dispatch(setLoading(false))
  }
}

// Replaces the existing same-test lab variant with the newly selected lab
// company's variant: removes remove_product_id, then adds incoming_product_sku,
// leaving exactly one of that test in the cart. Mirrors applySuggestions'
// cart-destroyed-on-last-item handling (a one-item cart is destroyed by the
// remove, so the incoming variant starts a fresh cart).
export const switchLabCompany = async (accessToken, cart, switchInfo, product, mutate, dispatch) => {
  const { remove_product_id: removeProductId, incoming_product_sku: sku } = switchInfo

  // Preserve the quantity of the lab line being switched out. The conflict is
  // detected on a same-product_group, different-service_category line, so its
  // quantity lives on that cart item (matched by data.id === removeProductId).
  // Reuse it for the incoming variant in both branches so a >1 quantity is not
  // silently dropped during the switch. Fall back to 1 if the line can't be found.
  const existingItem = cart?.contents?.find((item) => item.data?.id === removeProductId)
  const quantity = existingItem?.quantity || 1

  try {
    const removeResponse = await axios({
      method: 'delete',
      url: `/api/cart/${cart.uuid}/cart_items/${removeProductId}`,
      headers: { 'X-CSRF-Token': accessToken },
    })

    if (!removeResponse.data) {
      window.localStorage.removeItem('cart')
      await createCart(accessToken, product, quantity, false, mutate, dispatch)
    } else {
      await axios({
        method: 'post',
        url: `/api/cart/${cart.uuid}/cart_items`,
        headers: { 'X-CSRF-Token': accessToken },
        data: { sku, quantity },
      })
      // Await the SWR revalidation so the refreshed cart is in place before the
      // `finally` clears the loading state — otherwise the UI briefly shows the
      // pre-switch cart while loading is already false.
      await mutate()
    }

    dispatch(setError(null))
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error switching lab company:', error)
    dispatch(setError(error?.response?.data?.message || 'Something went wrong while switching lab companies. Please try again.'))
  } finally {
    dispatch(setLoading(false))
  }
}

export const handleQuantityChange = async (
  accessToken,
  cart,
  item,
  mutate,
  dispatch,
  newQuantity,
  oldQuantity,
) => {
  axios({
    method: 'patch',
    url: `/api/cart/${cart.uuid}/cart_items/${item.id}`,
    headers: { 'X-CSRF-Token': accessToken },
    data: { quantity: newQuantity },
  })
    .then(() => mutate())
    .then(() => {
      if (newQuantity > oldQuantity) {
        gaAddToCart(item, newQuantity - oldQuantity)
      } else if (newQuantity < oldQuantity) {
        gaRemoveFromCart(item, oldQuantity - newQuantity)
      }
    })
    .then(() => dispatch(setLoading(false)))
}

export default cartSlice.reducer
